import React, { useEffect, useState, useReducer, useCallback } from "react";
import {
  Box,
  Button,
  Link,
  Table,
  Td,
  Th,
  Tr,
  useToast,
  useDisclosure,
  TableContainer,
  Thead,
  VStack,
  HStack,
  Text,
  Badge,
  useBreakpointValue,
  Flex,
  Spacer
} from "@chakra-ui/react";
import {
  Card, CardHeader, CardTitle, Right, CardContent
} from "../../components/Card"
import client from "../../helpers/Client";
import "filepond/dist/filepond.min.css";
import CreateAttachmentModal from "./CreateModal";
import urlToId from "../../helpers/UrlToId";

function AttachmentRow({ attachment, dispatchAttachmentsChanged }) {
  const link = process.env.REACT_APP_API_BASE_URL + attachment.file_url.slice(1);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleDelete = (attachmentURL) => {
    const attachmentId = urlToId(attachmentURL);
    client
      .post("/attachments/" + attachmentId + "/delete/", { confirm: true })
      .then(() => {
        dispatchAttachmentsChanged();
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast({
            title: "Hata",
            description: "Başkasına ait eklentiyi silmeye yetkiniz yok.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        console.error("Bir hata oluştu:", error.message);
      });
  };

  if (isMobile) {
    return (
      <Card mb={3} mx={2} maxW="100%">
        <Box p={3}>
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between" align="start">
              <VStack align="start" spacing={1} flex={1}>
                <Link href={link} target="_blank" fontSize="sm" fontWeight="medium" color="blue.500">
                  {attachment.file_name}
                </Link>
                {attachment.description && (
                  <Text fontSize="xs" color="gray.600" noOfLines={2}>
                    {attachment.description}
                  </Text>
                )}
              </VStack>
              <Button 
                size="xs" 
                colorScheme="red" 
                variant="outline"
                onClick={() => handleDelete(attachment.url)}
                ml={2}
              >
                Sil
              </Button>
            </HStack>
            <HStack justify="space-between">
              <Badge colorScheme="blue" fontSize="xs">
                {attachment.created_by.username}
              </Badge>
            </HStack>
          </VStack>
        </Box>
      </Card>
    );
  }

  return (
    <Tr>
      <Td>
        <Link href={link} target="_blank">
          {attachment.file_name}
        </Link>
      </Td>
      <Td>{attachment.description}</Td>
      <Td>{attachment.created_by.username}</Td>
      <Td>
        <Button onClick={() => handleDelete(attachment.url)}>Sil</Button>
      </Td>
    </Tr>
  );
}

function AddAttachment({
  object_model,
  object_id,
  dispatchAttachmentsChanged,
  ...props
}) {
  const state = useDisclosure();
  return (
    <Box {...props}>
      <Button onClick={state.onOpen}>Yeni Eklenti Oluştur</Button>
      <CreateAttachmentModal
        state={state}
        object_model={object_model}
        object_id={object_id}
        {...{ dispatchAttachmentsChanged }}
      />
    </Box>
  );
}

function attachmentsChangedReducer(state) {
  return state + 1;
}

export default function Attachments({
  serviceName,
  object_model,
  object_id,
  header,
  ...props
}) {
  const [attachments, setAttachments] = useState([]);
  const [attachmentsChanged, dispatchAttachmentsChanged] = useReducer(
    attachmentsChangedReducer,
    0
  );
  const isMobile = useBreakpointValue({ base: true, md: false });

  const updateAttachments = useCallback(() => {
    client
      .get("/attachments/", { params: { object_model, object_id } })
      .then((response) => {
        setAttachments(response.data);
      });
  }, [object_model, object_id]);

  useEffect(() => {
    updateAttachments();
  }, [attachmentsChanged, updateAttachments]);

  return (
    <Card>
      <CardHeader isNaive={true}>
        <CardTitle>Eklentiler</CardTitle>
        <Right>
          <AddAttachment {...{ object_model, object_id, dispatchAttachmentsChanged }} />
        </Right>
      </CardHeader>
      <CardContent>
        {isMobile ? (
          <VStack spacing={2} align="stretch">
            {attachments.length === 0 ? (
              <Box p={4} textAlign="center">
                <Text color="gray.500" fontSize="sm">
                  Henüz eklenti bulunmuyor
                </Text>
              </Box>
            ) : (
              attachments.map((attachment) => (
                <AttachmentRow
                  attachment={attachment}
                  key={attachment.id}
                  dispatchAttachmentsChanged={dispatchAttachmentsChanged}
                />
              ))
            )}
          </VStack>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Dosya Adı</Th>
                  <Th>Açıklama</Th>
                  <Th>Gönderen</Th>
                  <Th>Aksiyon</Th>
                </Tr>
              </Thead>
              {attachments.map((attachment) => (
                <AttachmentRow
                  attachment={attachment}
                  key={attachment.id}
                  dispatchAttachmentsChanged={dispatchAttachmentsChanged}
                />
              ))}
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
